'use client'

// app/terms/page.js
// Halaman ini menampilkan Syarat dan Ketentuan untuk platform UMKMs.
// Dibuat dengan Next.js, Material-UI untuk komponen UI, dan TailwindCSS untuk styling.

import { Box, Container, Divider, Typography } from '@mui/material'
import * as React from 'react'

// Komponen untuk merender teks yang mengandung format tebal (**text**)
interface FormattedTextProps {
	text: string
}

// Data untuk setiap bagian Syarat dan Ketentuan
// Diambil dari dokumen yang sudah ada dan diatur dalam struktur data untuk kemudahan rendering.
const sections = [
	{
		title: 'A. Definisi',
		points: [
			'**PT UMKM Pintar Indonesia** adalah perseroan terbatas yang merupakan pemilik dan penyelenggara platform UMKMs.',
			'**UMKMs** adalah platform e-commerce berbasis situs web dan aplikasi yang berfungsi sebagai sarana untuk mempertemukan Penjual dan Pembeli dalam melakukan transaksi jual beli barang.',
			'**Pengguna** adalah setiap individu atau badan hukum yang mengakses platform UMKMs, termasuk namun tidak terbatas pada Pembeli dan Penjual yang telah terdaftar.',
			'**Pembeli** adalah Pengguna terdaftar yang melakukan permintaan atas Barang yang dijual oleh Penjual di platform UMKMs.',
			'**Penjual** adalah Pengguna terdaftar yang membuka toko dan/atau melakukan penawaran atas suatu Barang kepada para Pengguna di platform UMKMs.',
			'**Barang** adalah benda berwujud atau produk fisik yang dapat diantar atau memenuhi kriteria pengiriman oleh perusahaan jasa pengiriman Barang.',
			'**Syarat dan Ketentuan** adalah perjanjian antara Pengguna dan PT UMKM Pintar Indonesia yang berisikan seperangkat peraturan yang mengatur hak, kewajiban, dan tanggung jawab Pengguna serta PT UMKM Pintar Indonesia.',
			'**Rekening Resmi UMKMs** adalah rekening bersama yang disepakati oleh PT UMKM Pintar Indonesia dan Pengguna untuk proses transaksi jual beli di platform UMKMs.'
		]
	},
	{
		title: 'B. Akun, Kata Sandi, dan Keamanan',
		points: [
			'Pengguna dengan ini menyatakan bahwa ia adalah individu yang cakap dan mampu untuk mengikatkan dirinya dalam sebuah perjanjian yang sah menurut hukum.',
			'UMKMs tidak memungut biaya pendaftaran kepada Pengguna.',
			'Pengguna bertanggung jawab secara pribadi untuk menjaga kerahasiaan akun dan kata sandi (password) untuk semua aktivitas yang terjadi dalam akun Pengguna.',
			'Kami tidak akan pernah meminta kata sandi akun Anda untuk alasan apa pun. Oleh karena itu, Kami mengimbau Pengguna agar tidak memberikan kata sandi kepada pihak mana pun, baik kepada pihak ketiga maupun kepada pihak yang mengatasnamakan PT UMKM Pintar Indonesia.',
			'Pengguna setuju untuk segera memberitahukan kepada Kami jika terdapat dugaan penggunaan akun Anda yang tidak sah.',
			'PT UMKM Pintar Indonesia berhak untuk membekukan atau menghentikan akun Pengguna apabila ditemukan adanya aktivitas yang melanggar ketentuan hukum atau Syarat dan Ketentuan ini.'
		]
	},
	{
		title: 'C. Transaksi Jual Beli',
		points: [
			'Penjual dilarang memanipulasi harga Barang dengan tujuan apa pun.',
			'Penjual dilarang menawarkan Barang yang dilarang untuk diperdagangkan berdasarkan peraturan perundang-undangan yang berlaku di Indonesia.',
			'Penjual wajib memberikan deskripsi, foto, dan informasi produk yang akurat dan tidak menyesatkan.',
			'Pembeli memahami dan menyetujui bahwa ketersediaan stok Barang merupakan tanggung jawab Penjual. Apabila stok tidak tersedia, maka Penjual berhak menolak pesanan, dan pembayaran atas barang tersebut akan dikembalikan kepada Pembeli.',
			'Setiap transaksi yang dilakukan melalui UMKMs wajib menggunakan Rekening Resmi UMKMs sebagai metode pembayaran yang sah.',
			'Saat melakukan pembelian, Pembeli menyetujui bahwa: Pembeli bertanggung jawab untuk membaca, memahami, dan menyetujui informasi/deskripsi keseluruhan Barang sebelum membuat komitmen untuk membeli. Pembeli masuk ke dalam kontrak yang mengikat secara hukum untuk membeli Barang ketika Pembeli melakukan transaksi.',
			'UMKMs bertindak sebagai fasilitator untuk meneruskan dana pembayaran dari Pembeli kepada Penjual setelah tahapan transaksi selesai dan terkonfirmasi.',
			'UMKMs memiliki kewenangan untuk menahan pembayaran atau membatalkan transaksi jika ditemukan adanya indikasi penipuan atau pelanggaran terhadap Syarat dan Ketentuan.'
		]
	},
	{
		title: 'D. Harga',
		points: [
			'Harga Barang yang terdapat dalam platform UMKMs adalah harga yang ditetapkan oleh Penjual.',
			'Pengguna memahami bahwa kesalahan pengetikan informasi harga yang disebabkan oleh Penjual bukan merupakan tanggung jawab PT UMKM Pintar Indonesia.',
			'Dengan melakukan pemesanan melalui UMKMs, Pengguna setuju untuk membayar total biaya yang harus dibayarkan sebagaimana tertera dalam halaman pembayaran, yang terdiri dari harga barang, ongkos kirim, dan biaya lain yang mungkin timbul dan akan diuraikan secara transparan pada halaman pembayaran.'
		]
	},
	{
		title: 'E. Konten',
		points: [
			'Dalam menggunakan setiap fitur dan/atau layanan UMKMs, Pengguna dilarang untuk mengunggah atau menggunakan kata-kata, komentar, gambar, atau konten apa pun yang mengandung unsur SARA, diskriminasi, merendahkan atau menyudutkan orang lain, vulgar, bersifat ancaman, atau hal-hal lain yang dapat dianggap tidak sesuai dengan nilai dan norma sosial serta bertentangan dengan peraturan yang berlaku.',
			'Pengguna bertanggung jawab penuh atas konten yang mereka unggah. UMKMs berhak untuk melakukan tindakan yang diperlukan, termasuk penghapusan konten dan penutupan akun, atas pelanggaran ketentuan ini.',
			'Pengguna dengan ini memberikan PT UMKM Pintar Indonesia hak non-eksklusif, bebas royalti, untuk menggunakan, menyalin, dan menampilkan konten yang diunggah oleh Pengguna untuk tujuan promosi dan pengembangan platform UMKMs.'
		]
	},
	{
		title: 'F. Batasan Tanggung Jawab',
		points: [
			'PT UMKM Pintar Indonesia adalah portal web dengan model bisnis marketplace, yang menyediakan layanan kepada Pengguna untuk menjadi Penjual maupun Pembeli di platform UMKMs.',
			'PT UMKM Pintar Indonesia tidak pernah bertindak sebagai pemilik atau penjual dari barang-barang yang ditawarkan oleh para Penjual. Transaksi yang terjadi adalah antara Penjual dan Pembeli.',
			'Kami selalu berupaya untuk menjaga Layanan UMKMs aman, nyaman, dan berfungsi dengan baik, namun kami tidak dapat menjamin operasi atau akses tanpa gangguan.',
			'Sejauh diizinkan oleh hukum, PT UMKM Pintar Indonesia (termasuk Induk Perusahaan, direktur, dan karyawan) tidak bertanggung jawab, dan Anda setuju untuk tidak menuntut kami bertanggung jawab, atas segala kerusakan atau kerugian (termasuk namun tidak terbatas pada hilangnya uang, reputasi, keuntungan, atau kerugian tak berwujud lainnya) yang diakibatkan secara langsung atau tidak langsung dari: Penggunaan atau ketidakmampuan Anda dalam menggunakan Layanan UMKMs. Kualitas, keaslian, atau keamanan Barang. Proses pengiriman Barang. Kelalaian atau tindakan yang dilakukan oleh Pengguna lain. Sengketa yang terjadi antar Pengguna.'
		]
	},
	{
		title: 'G. Pelepasan dan Ganti Rugi',
		points: [
			'Anda akan melepaskan PT UMKM Pintar Indonesia dari tuntutan ganti rugi dan menjaga kami (termasuk Induk Perusahaan, direktur, dan karyawan) dari setiap klaim atau tuntutan, termasuk biaya hukum yang wajar, yang dibuat oleh pihak ketiga yang timbul akibat pelanggaran Anda terhadap Syarat dan Ketentuan ini atau pelanggaran Anda terhadap hukum atau hak-hak pihak ketiga.'
		]
	},
	{
		title: 'H. Pilihan Hukum dan Penyelesaian Sengketa',
		points: [
			'Perjanjian ini akan diatur oleh dan ditafsirkan sesuai dengan hukum Republik Indonesia. Anda setuju bahwa setiap dan semua sengketa yang timbul dari perjanjian ini akan diselesaikan terlebih dahulu melalui musyawarah untuk mufakat. Apabila mufakat tidak tercapai, sengketa akan diselesaikan melalui yurisdiksi Pengadilan Negeri yang relevan di wilayah Republik Indonesia.'
		]
	},
	{
		title: 'I. Pembaruan',
		points: [
			'Syarat dan Ketentuan ini dapat diubah dan/atau diperbarui dari waktu ke waktu tanpa pemberitahuan sebelumnya. PT UMKM Pintar Indonesia menyarankan agar Anda membaca secara saksama dan memeriksa halaman Syarat dan Ketentuan ini secara berkala untuk mengetahui perubahan apa pun. Dengan tetap mengakses dan menggunakan layanan UMKMs, maka Pengguna dianggap menyetujui perubahan-perubahan dalam Syarat dan Ketentuan.'
		]
	}
]

const FormattedText: React.FC<FormattedTextProps> = ({ text }) => {
	// Memisahkan teks berdasarkan pola **...** untuk menerapkan style tebal
	const parts = text.split(/(\*\*.*?\*\*)/g).filter((part): part is string => part !== '')
	return (
		<React.Fragment>
			{parts.map((part, index) =>
				part.startsWith('**') && part.endsWith('**') ? (
					<Box component='span' fontWeight='bold' key={index}>
						{part.slice(2, -2)}
					</Box>
				) : (
					part
				)
			)}
		</React.Fragment>
	)
}

export const TNCSection: React.FC = () => {
	return (
		<main>
			<Container maxWidth='lg'>
				<Box>
					{/* Header utama halaman */}
					<Box className='mb-8 text-center'>
						<Typography variant='h3' component='h1' className='font-bold text-gray-800'>
							Syarat dan Ketentuan Platform UMKMs
						</Typography>
						<Typography variant='subtitle1' className='mt-2 text-gray-500'>
							Terakhir diperbarui: 12 Juni 2025
						</Typography>
					</Box>

					<Divider className='mb-8' />

					{/* Intro */}
					<Box className='mb-10'>
						<Typography variant='h6' component='p' className='font-semibold mb-4 text-gray-700'>
							Selamat datang di UMKMs!
						</Typography>
						<Typography variant='body1' className='text-gray-600 leading-relaxed'>
							Syarat dan Ketentuan ini adalah perjanjian yang mengikat secara hukum antara Pengguna (&quot;Anda&quot;) dan
							<Box component='span' className='font-bold'>
								PT UMKM Pintar Indonesia
							</Box>{' '}
							(&quot;Kami&quot;), selaku pemilik dan pengelola platform UMKMs. Dengan mendaftar dan/atau menggunakan platform UMKMs, Anda dianggap telah membaca, memahami, menyetujui, dan sepakat
							untuk mematuhi semua isi dalam Syarat dan Ketentuan ini beserta perubahannya di kemudian hari.
						</Typography>
						<Typography variant='body1' className='mt-4 text-gray-600 leading-relaxed'>
							Jika Anda tidak menyetujui salah satu, sebagian, atau seluruh isi Syarat dan Ketentuan ini, Anda tidak diperkenankan untuk menggunakan layanan di platform Kami.
						</Typography>
					</Box>

					{/* Konten dinamis dari array sections */}
					<Box className='space-y-10'>
						{sections.map((section, sectionIndex) => (
							<Box key={sectionIndex}>
								<Typography variant='h4' component='h2' className='font-bold mb-4 border-b pb-2 border-gray-200 text-gray-800'>
									{section.title}
								</Typography>
								<ul className='space-y-4 list-decimal pl-6'>
									{section.points.map((point, pointIndex) => (
										<li key={pointIndex}>
											<Typography variant='body1' className='text-gray-600 leading-relaxed'>
												<FormattedText text={point} />
											</Typography>
										</li>
									))}
								</ul>
							</Box>
						))}
					</Box>
				</Box>
			</Container>
		</main>
	)
}
